import * as p from '@clack/prompts'
import { spinner } from '@core-engine/utils/spinner.js'
import pc from 'picocolors'

import { ensureVercelAuth } from './auth.js'
import { blobStoreName, provisionBlobStore, provisionBlobStoreInteractive } from './blob.js'
import {
  deployVercelProject,
  ensureVercelJsonFramework,
  guardProjectForDeploy,
  syncVercelProductionEnv
} from './deploy.js'
import { guardEnvLocal } from './env-guard.js'
import { pullVercelEnvValue } from './env-pull.js'
import { provisionNeon, provisionNeonInteractive, readDbUrlFromDotenv } from './neon.js'
import { createVercelProject, readLinkedProjectId } from './project.js'
import { resolveVercelRunner } from './runner.js'
import type {
  AuthFailureReason,
  BlobFailureReason,
  BlobProvisionResult,
  DeployFailureReason,
  NeonFailureReason,
  NeonProvisionResult,
  VercelBlobFlowResult,
  VercelDeployFlowResult,
  VercelNeonFlowResult,
  VercelRunner
} from './types.js'

/**
 * Top-level orchestrator for the Vercel-backed Neon flow during `betterstart
 * init`: resolve a runner → ensure auth (interactive login link) → provision a
 * Neon database through Vercel Marketplace → pull DATABASE_URL through a temp
 * env file. The flow sets up the database only — email (Resend) stays a manual,
 * prompt-driven setup because Resend requires a dashboard-configured domain.
 *
 * When interactive, provisioning first runs quietly with captured JSON output
 * and only re-runs attached to the terminal when Vercel needs the user to accept
 * marketplace terms or pick a plan in its own prompts. Non-interactive `--yes`
 * + token runs the same captured JSON add path without the terminal fallback.
 *
 * Never throws. Provisioning failures resolve to `{ ok: false }` so the caller
 * can fall back to manual connection-string entry; manual paste is only needed
 * when the resource exists but Vercel does not return a usable DATABASE_URL.
 */
export async function runVercelNeonFlow(options: {
  cwd: string
  projectName: string
  interactive: boolean
  plan?: string
  env?: NodeJS.ProcessEnv
}): Promise<VercelNeonFlowResult> {
  const env = options.env ?? process.env
  // `vercel link` can inject VERCEL_OIDC_TOKEN into `.env.local`; the database
  // URL is captured through a temp env pull and persisted by init itself.
  const envGuard = guardEnvLocal(options.cwd)

  try {
    const runnerSpinner = spinner()
    runnerSpinner.start('Checking for the Vercel CLI')
    const runner = await resolveVercelRunner()
    runnerSpinner.clear()

    // Auth owns its own spinners and stops them before any interactive login.
    const auth = await ensureVercelAuth(runner, options.cwd, { env })
    if (!auth.authed) {
      p.log.warn(authFailureMessage(auth.reason))
      return { ok: false }
    }

    await ensureLinkedProject(runner, options.cwd, options.projectName, env, auth.username)

    const neon = await provisionNeonForMode(runner, options)
    if (neon.failure) {
      p.log.warn(neonFailureMessage(neon.failure))
      if (neon.detail) p.log.message(pc.dim(neon.detail))
      return { ok: false }
    }

    const databaseUrl = await pullNeonDatabaseUrl(runner, options.cwd, env)

    // The signed-in note can be erased later (once init moves on to its next
    // prompt) only when everything printed after it is known: a clean success
    // prints nothing below it (project linking clears silently). The
    // terminal-attached Neon fallback and the no-DATABASE_URL warning put
    // unknown rows under it.
    const dismissSignedInNote =
      databaseUrl && auth.dismissNote && !neon.usedTerminalFallback
        ? () => auth.dismissNote?.({ linesBelow: 0 })
        : undefined

    return {
      ok: true,
      databaseUrl,
      dashboardUrl: neon.dashboardUrl,
      resourceUrl: neon.resourceUrl,
      dismissSignedInNote
    }
  } catch (error) {
    p.log.warn(
      `Vercel provisioning failed: ${error instanceof Error ? error.message : String(error)}`
    )
    return { ok: false }
  } finally {
    envGuard.restore()
  }
}

/**
 * Vercel-backed Blob storage flow for `betterstart init`: resolve a runner →
 * ensure auth → reuse or create a linked Vercel project → create a Blob store
 * connected to that project and capture its BLOB_READ_WRITE_TOKEN.
 *
 * Never throws. Any failure at any step resolves to `{ ok: false }`; callers
 * continue without a Blob token (never a manual token prompt) — name conflicts
 * are already absorbed by the provisioner's version-suffix retries.
 */
export async function runVercelBlobFlow(options: {
  cwd: string
  projectName: string
  interactive: boolean
  env?: NodeJS.ProcessEnv
}): Promise<VercelBlobFlowResult> {
  const env = options.env ?? process.env
  // `vercel blob create-store` runs a full env pull over `.env.local` after
  // connecting the store (no opt-out flag), and `vercel link` injects
  // VERCEL_OIDC_TOKEN; the token is captured via a temp env pull instead, so
  // snapshot the file and restore it once provisioning is done.
  const envGuard = guardEnvLocal(options.cwd)

  try {
    const runnerSpinner = spinner()
    runnerSpinner.start('Checking for the Vercel CLI')
    const runner = await resolveVercelRunner()
    runnerSpinner.clear()

    // Auth owns its own spinners and stops them before any interactive login.
    // Quiet when already signed in — the Neon flow (or a prior session)
    // already announced the account, and this runs mid-prompt-flow.
    const auth = await ensureVercelAuth(runner, options.cwd, { env, quietIfAuthed: true })
    if (!auth.authed) {
      p.log.warn(authFailureMessage(auth.reason))
      return { ok: false }
    }

    await ensureLinkedProject(runner, options.cwd, options.projectName, env, auth.username)

    const blob = await provisionBlobForMode(runner, options)
    if (blob.failure || !blob.token) {
      p.log.warn(blobFailureMessage(blob.failure))
      if (blob.detail) p.log.message(pc.dim(blob.detail))
      return { ok: false }
    }

    return {
      ok: true,
      token: blob.token,
      blobStoreName: blob.storeName
    }
  } catch (error) {
    p.log.warn(
      `Vercel provisioning failed: ${error instanceof Error ? error.message : String(error)}`
    )
    return { ok: false }
  } finally {
    envGuard.restore()
  }
}

/**
 * Post-scaffold production deploy for `betterstart init`: resolve a runner →
 * quiet auth re-check → reuse an existing Vercel link or create + link one
 * after deploy is accepted → push the missing production env vars from
 * `.env.local` → `vercel deploy --prod`. The
 * Next.js config was already scaffolded deploy-ready by `scaffoldNextConfig`;
 * the deploy uses it as-is.
 *
 * Never throws. Any failure resolves to `{ ok: false }` after printing the
 * manual deploy command, so init always finishes.
 */
export async function runVercelDeployFlow(options: {
  cwd: string
  projectName: string
  env?: NodeJS.ProcessEnv
}): Promise<VercelDeployFlowResult> {
  const env = options.env ?? process.env
  // Deploy itself does not write `.env.local`, but `vercel link` (manual-setup
  // path) injects VERCEL_OIDC_TOKEN into it — same guard as the other flows.
  const envGuard = guardEnvLocal(options.cwd)

  try {
    const runnerSpinner = spinner()
    runnerSpinner.start('Checking for the Vercel CLI')
    const runner = await resolveVercelRunner()
    runnerSpinner.clear()

    // Quiet when already signed in; database/storage setup or a prior session
    // may already have announced the account.
    const auth = await ensureVercelAuth(runner, options.cwd, { env, quietIfAuthed: true })
    if (!auth.authed) {
      p.log.warn(authFailureMessage(auth.reason))
      printManualDeployHint()
      return { ok: false }
    }

    await ensureLinkedProject(runner, options.cwd, options.projectName, env, auth.username)

    const envSpinner = spinner()
    envSpinner.start('Syncing environment variables to Vercel')
    const sync = await syncVercelProductionEnv(runner, options.cwd, env)
    envSpinner.clear()
    for (const key of sync.failed) {
      p.log.warn(
        `Could not set ${pc.cyan(key)} on Vercel — add it in the project's environment settings.`
      )
    }

    // A CLI-created project has no framework preset ("Other"), which makes
    // every route 404; a vercel.json pins the Next.js preset per deployment.
    ensureVercelJsonFramework(options.cwd)

    // Uploadable package.json: strip the dev-only betterstart-cli entry so the
    // remote npm install cannot die on its workspace:/unpublished spec, then
    // regenerate pnpm-lock.yaml to match — Vercel's frozen-lockfile install
    // rejects a lockfile that still lists the removed dep. The lockfile
    // refresh hits the registry, so cover it with a spinner.
    const guardSpinner = spinner()
    guardSpinner.start('Preparing the project for deploy')
    const packageGuard = await guardProjectForDeploy(options.cwd)
    guardSpinner.clear()
    if (packageGuard.lockfile === 'failed') {
      p.log.warn(
        'Could not refresh pnpm-lock.yaml after removing betterstart-cli — the remote install may fail.'
      )
    }
    for (const dep of packageGuard.localSpecDeps) {
      p.log.warn(
        `Dependency ${pc.cyan(dep)} uses a local spec that cannot install on Vercel — the remote build may fail.`
      )
    }

    const deploySpinner = spinner()
    deploySpinner.start('Deploying to Vercel')
    let deploy: Awaited<ReturnType<typeof deployVercelProject>>
    try {
      deploy = await deployVercelProject(runner, options.cwd, {
        env,
        onLogLine: (line) => deploySpinner.message(line)
      })
    } finally {
      packageGuard.restore()
    }
    if (deploy.failure) {
      deploySpinner.stop(`${pc.yellow('▲')} ${deployFailureMessage(deploy.failure)}`)
      if (deploy.detail) p.log.message(pc.dim(deploy.detail))
      printManualDeployHint()
      return { ok: false }
    }

    // Vercel owns production-domain normalization. A project name may contain
    // dots or other characters that are transformed in the assigned domain, so
    // never reconstruct a URL from `.vercel/project.json`.
    const url = deploy.url
    deploySpinner.stop(url ? `Deployed ${pc.cyan(url)}` : 'Deployed to Vercel')
    return { ok: true, url, syncedEnvKeys: sync.synced }
  } catch (error) {
    p.log.warn(`Vercel deploy failed: ${error instanceof Error ? error.message : String(error)}`)
    printManualDeployHint()
    return { ok: false }
  } finally {
    envGuard.restore()
  }
}

function printManualDeployHint(): void {
  p.log.info(`You can deploy manually: ${pc.cyan('vercel deploy --prod')}`)
}

/**
 * Reuse the existing `.vercel/project.json` link or create + link a fresh
 * project. Silent on completion — the spinner clears without a
 * `Linked Vercel project …` line, keeping init's no-interim-lines rule.
 */
async function ensureLinkedProject(
  runner: VercelRunner,
  cwd: string,
  projectName: string,
  env?: NodeJS.ProcessEnv,
  scope?: string
): Promise<void> {
  if (readLinkedProjectId(cwd)) return

  const projectSpinner = spinner()
  projectSpinner.start(`Creating a Vercel project ${pc.cyan(projectName)}`)
  const project = await createVercelProject(runner, cwd, projectName, { env, scope })
  projectSpinner.clear()

  if (!project.linked || !project.projectId) {
    throw new Error(`Could not link Vercel project ${project.name} to this directory.`)
  }
}

async function pullNeonDatabaseUrl(
  runner: VercelRunner,
  cwd: string,
  env?: NodeJS.ProcessEnv
): Promise<string | undefined> {
  const neonSpinner = spinner()
  neonSpinner.start('Retrieving DATABASE_URL from Vercel')
  const databaseUrl = await pullVercelEnvValue(runner, cwd, readDbUrlFromDotenv, env)
  if (!databaseUrl) {
    neonSpinner.stop(`${pc.yellow('▲')} No DATABASE_URL came back from Vercel`)
    return undefined
  }

  neonSpinner.clear()
  return databaseUrl
}

function provisionBlobForMode(
  runner: VercelRunner,
  options: { cwd: string; projectName: string; interactive: boolean; env?: NodeJS.ProcessEnv }
): Promise<BlobProvisionResult> {
  const opts = { name: blobStoreName(options.projectName), env: options.env }
  return options.interactive
    ? provisionBlobStoreInteractive(runner, options.cwd, opts)
    : provisionBlobStore(runner, options.cwd, opts)
}

function provisionNeonForMode(
  runner: VercelRunner,
  options: { cwd: string; interactive: boolean; plan?: string; env?: NodeJS.ProcessEnv }
): Promise<NeonProvisionResult> {
  const opts = { plan: options.plan, env: options.env }
  return options.interactive
    ? provisionNeonInteractive(runner, options.cwd, opts)
    : provisionNeon(runner, options.cwd, opts)
}

function authFailureMessage(reason?: AuthFailureReason): string {
  switch (reason) {
    case 'login-cancelled':
      return 'Vercel sign-in was cancelled.'
    case 'login-failed':
      return 'Could not verify your Vercel sign-in.'
    case 'timeout':
      return 'Vercel sign-in timed out.'
    case 'no-runner':
      return 'The Vercel CLI is not available.'
    default:
      return 'Could not sign in to Vercel.'
  }
}

function blobFailureMessage(reason?: BlobFailureReason): string {
  switch (reason) {
    case 'env-pull-empty':
      return 'Created a Blob store, but no BLOB_READ_WRITE_TOKEN came back from Vercel.'
    case 'timeout':
      return 'Vercel Blob provisioning timed out.'
    default:
      return 'Vercel Blob provisioning did not complete.'
  }
}

function deployFailureMessage(reason?: DeployFailureReason): string {
  switch (reason) {
    case 'timeout':
      return 'Vercel deploy timed out.'
    default:
      return 'Vercel deploy did not complete.'
  }
}

function neonFailureMessage(reason?: NeonFailureReason): string {
  switch (reason) {
    case 'terms-required':
      return 'Provisioning needs the Neon marketplace terms accepted — falling back to manual entry.'
    case 'claim-required':
      return 'The Neon resource needs to be claimed — falling back to manual entry.'
    case 'timeout':
      return 'Neon provisioning timed out — falling back to manual entry.'
    default:
      return 'Neon provisioning did not complete — falling back to manual entry.'
  }
}
